import { useState } from 'react';
import { Button, Input, Paragraph, Spinner, Text, XStack, YStack } from 'tamagui';
import { SkeletonList } from '@mrrmlab/ui';
import type { MailTag, MailTagRule } from '@mrrmlab/api-client';
import { useMailTags, useRenameTag, useMergeTag, useDeleteTag, useTagRules, useCreateTagRule, useDeleteTagRule } from '../mail/hooks';

type Mode = { type: 'none' } | { type: 'rename'; tagId: string } | { type: 'merge'; tagId: string } | { type: 'add-rule'; tagId: string };

export function EinstellungenTagsRoute() {
  const { data: tags = [], isLoading } = useMailTags();
  const { data: allRules = [] } = useTagRules();
  const { mutateAsync: renameTag, isPending: renaming } = useRenameTag();
  const { mutateAsync: mergeTag, isPending: merging } = useMergeTag();
  const { mutateAsync: deleteTag } = useDeleteTag();
  const { mutateAsync: createRule, isPending: creatingRule } = useCreateTagRule();
  const { mutateAsync: deleteRule } = useDeleteTagRule();
  const [mode, setMode] = useState<Mode>({ type: 'none' });
  const [renameValue, setRenameValue] = useState('');
  const [mergeSearch, setMergeSearch] = useState('');
  const [ruleListTitle, setRuleListTitle] = useState('');
  const [ruleTitleTemplate, setRuleTitleTemplate] = useState('{subject}');

  function startRename(tag: MailTag) {
    setMode({ type: 'rename', tagId: tag.id });
    setRenameValue(tag.name);
  }

  function startMerge(tag: MailTag) {
    setMode({ type: 'merge', tagId: tag.id });
    setMergeSearch('');
  }

  function startAddRule(tag: MailTag) {
    setMode({ type: 'add-rule', tagId: tag.id });
    setRuleListTitle('');
    setRuleTitleTemplate('{subject}');
  }

  function cancel() {
    setMode({ type: 'none' });
  }

  async function handleRename(tagId: string) {
    if (!renameValue.trim()) return;
    await renameTag({ tagId, name: renameValue.trim() });
    setMode({ type: 'none' });
  }

  async function handleMerge(sourceTagId: string, targetTagId: string) {
    await mergeTag({ sourceTagId, targetTagId });
    setMode({ type: 'none' });
  }

  async function handleDelete(tag: MailTag) {
    if (!window.confirm(`"${tag.name}" wirklich löschen? Alle zugewiesenen Nachrichten verlieren diesen Tag.`)) return;
    await deleteTag(tag.id);
    if (mode.type !== 'none' && mode.tagId === tag.id) setMode({ type: 'none' });
  }

  async function handleAddRule(tagId: string) {
    if (!ruleListTitle.trim() || !ruleTitleTemplate.trim()) return;
    await createRule({ tagId, actionType: 'create-todo', config: { listTitle: ruleListTitle.trim(), titleTemplate: ruleTitleTemplate.trim() } });
    setMode({ type: 'none' });
  }

  if (isLoading) {
    return (
      <YStack padding="$4" gap="$4" maxWidth={480}>
        <SkeletonList count={6} />
      </YStack>
    );
  }

  const currentTag = mode.type !== 'none' ? tags.find((t) => t.id === mode.tagId) : undefined;

  if (mode.type === 'merge' && currentTag) {
    const lq = mergeSearch.toLowerCase();
    const targets = tags.filter((t) => t.id !== currentTag.id && (!lq || t.name.toLowerCase().includes(lq)));
    return (
      <YStack padding="$4" gap="$4" maxWidth={640}>
        <XStack alignItems="center" gap="$3">
          <Button size="$3" chromeless onPress={cancel}>← Zurück</Button>
          <Text fontSize="$6" fontWeight="700">&ldquo;{currentTag.name}&rdquo; zusammenführen</Text>
        </XStack>
        <Paragraph color="$color10">Ziel-Tag auswählen — der Quell-Tag wird gelöscht, alle Nachrichten werden dem Ziel zugeordnet.</Paragraph>
        <Input size="$3" placeholder="Tag suchen…" value={mergeSearch} onChangeText={setMergeSearch} />
        <YStack gap="$2">
          {targets.map((t) => (
            <XStack
              key={t.id}
              paddingVertical="$2"
              paddingHorizontal="$3"
              borderRadius="$3"
              borderWidth={1}
              borderColor="$borderColor"
              alignItems="center"
              justifyContent="space-between"
              hoverStyle={{ backgroundColor: '$color3' }}
              cursor="pointer"
            >
              <Text fontSize="$4">{t.name} <Text color="$color10">({t.messageCount ?? 0})</Text></Text>
              <Button size="$2" theme="blue" onPress={() => handleMerge(currentTag.id, t.id)} disabled={merging}>
                Zusammenführen
              </Button>
            </XStack>
          ))}
          {!targets.length && <Paragraph color="$color10">Keine weiteren Tags gefunden.</Paragraph>}
        </YStack>
      </YStack>
    );
  }

  if (mode.type === 'add-rule' && currentTag) {
    return (
      <YStack padding="$4" gap="$4" maxWidth={480}>
        <XStack alignItems="center" gap="$3">
          <Button size="$3" chromeless onPress={cancel}>← Zurück</Button>
          <Text fontSize="$6" fontWeight="700">Regel für &ldquo;{currentTag.name}&rdquo;</Text>
        </XStack>
        <Paragraph color="$color10">Wenn eine Mail mit diesem Tag bestätigt wird, wird ein Todo erstellt.</Paragraph>
        <YStack gap="$2">
          <Text fontSize="$3" fontWeight="600">Liste (wird erstellt falls nicht vorhanden)</Text>
          <Input size="$3" placeholder="z.B. Mail-Todos" value={ruleListTitle} onChangeText={setRuleListTitle} />
        </YStack>
        <YStack gap="$2">
          <Text fontSize="$3" fontWeight="600">Todo-Titel (Variablen: {'{subject}'}, {'{from}'}, {'{tagName}'})</Text>
          <Input size="$3" placeholder="z.B. {subject}" value={ruleTitleTemplate} onChangeText={setRuleTitleTemplate} />
        </YStack>
        <XStack gap="$2">
          <Button
            size="$3"
            theme="blue"
            onPress={() => handleAddRule(currentTag.id)}
            disabled={creatingRule || !ruleListTitle.trim() || !ruleTitleTemplate.trim()}
          >
            {creatingRule ? <Spinner size="small" /> : 'Regel speichern'}
          </Button>
          <Button size="$3" chromeless onPress={cancel}>Abbrechen</Button>
        </XStack>
      </YStack>
    );
  }

  const rulesByTag = allRules.reduce<Record<string, MailTagRule[]>>((acc, r) => {
    (acc[r.tagId] ??= []).push(r);
    return acc;
  }, {});

  return (
    <YStack padding="$4" gap="$4" maxWidth={640}>
      <Text fontSize="$7" fontWeight="700">Tag-Verwaltung</Text>
      {!tags.length ? (
        <Paragraph color="$color10">Keine Tags vorhanden.</Paragraph>
      ) : (
        <YStack gap="$2">
          {tags.map((tag) => {
            const isRenaming = mode.type === 'rename' && mode.tagId === tag.id;
            const rules = rulesByTag[tag.id] ?? [];
            return (
              <YStack key={tag.id} paddingVertical="$2" borderBottomWidth={1} borderBottomColor="$borderColor" gap="$2">
                <XStack alignItems="center" gap="$2">
                  <Text flex={1} fontSize="$4" fontWeight="500">{tag.name}</Text>
                  <Text fontSize="$3" color="$color10">{tag.messageCount ?? 0} Nachrichten</Text>
                </XStack>
                {isRenaming ? (
                  <XStack gap="$2" alignItems="center">
                    <Input
                      flex={1}
                      size="$3"
                      value={renameValue}
                      onChangeText={setRenameValue}
                      autoFocus
                      onSubmitEditing={() => handleRename(tag.id)}
                    />
                    <Button size="$2" onPress={() => handleRename(tag.id)} disabled={renaming || !renameValue.trim()}>
                      {renaming ? <Spinner size="small" /> : 'Speichern'}
                    </Button>
                    <Button size="$2" chromeless onPress={cancel}>Abbrechen</Button>
                  </XStack>
                ) : (
                  <XStack gap="$2">
                    <Button size="$2" chromeless onPress={() => startRename(tag)}>Umbenennen</Button>
                    <Button size="$2" chromeless onPress={() => startMerge(tag)}>Zusammenführen</Button>
                    <Button size="$2" chromeless color="$red10" onPress={() => handleDelete(tag)}>Löschen</Button>
                  </XStack>
                )}
                {rules.length > 0 && (
                  <YStack gap="$1" paddingLeft="$2">
                    {rules.map((rule) => (
                      <XStack key={rule.id} alignItems="center" gap="$2">
                        <Text fontSize="$2" color="$color10" flex={1}>
                          → Todo in &ldquo;{rule.config.listTitle}&rdquo;: {rule.config.titleTemplate}
                        </Text>
                        <Button size="$1" chromeless color="$red10" onPress={() => deleteRule(rule.id)}>✕</Button>
                      </XStack>
                    ))}
                  </YStack>
                )}
                <Button size="$2" chromeless color="$blue10" onPress={() => startAddRule(tag)} alignSelf="flex-start">
                  + Regel hinzufügen
                </Button>
              </YStack>
            );
          })}
        </YStack>
      )}
    </YStack>
  );
}
